import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
} from "@mui/material";
import { StreamChat } from "stream-chat";
import { makeStyles } from "tss-react/mui";

import {
  BadgeCountsContext,
  formatApiRequestError,
  handleSessionCheck,
  UserContext,
} from "../../../helpers";
import { ErrorType } from "../../../types";
import { useTitle } from "../../hooks/useTitle";
import {
  STREAM_CONNECTION_TIMEOUT,
  STREAM_SERVICE_TIMEOUT,
  TIMER_DEBOUNCE_TIME,
  UNREAD_REFRESH_DEBOUNCE_MS,
} from "../../messaging/constant";
import ModalServerError from "../../modals/ModalServerError";
import PageHeader from "../../pageheader/PageHeader";
import MessageInbox from "./MessageInbox/MessageInbox";

const useStyles = makeStyles()((theme) => ({
  pageLayout: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
  },
  inboxWrapper: {
    flex: 1,
    minHeight: 0,
    position: "relative",
    borderTop: "2px solid transparent",
  },
  syncingTopBorder: {
    position: "absolute",
    top: "-2px",
    left: 0,
    right: 0,
    height: "2px",
    zIndex: 1,
    pointerEvents: "none",
    borderTopLeftRadius: "6px",
    borderTopRightRadius: "6px",
    "& .MuiLinearProgress-root": {
      height: "2px",
      borderRadius: 0,
      backgroundColor: "transparent",
    },
    "& .MuiLinearProgress-bar": {
      borderRadius: 0,
    },
  },
}));

type UnreadEventUnsubscribeHandler =
  | (() => void)
  | {
      unsubscribe: () => void;
    };

const MessageInboxView = () => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showServerErrorModal, setShowServerErrorModal] = useState(false);
  const [isBadgeSyncing, setIsBadgeSyncing] = useState(true);
  const disconnectionTimeout = useRef<number | undefined>(undefined);
  const unreadEventUnsubscribeHandlers = useRef<
    UnreadEventUnsubscribeHandler[]
  >([]);
  const debouncedRefreshUnreadCountsRef = useRef<ReturnType<
    typeof debounce
  > | null>(null);
  const [chatClient, setChatClient] = useState<StreamChat | null>(null);

  const { classes } = useStyles();
  const { user } = useContext(UserContext);
  const { refreshStreamUnreadCounts } = useContext(BadgeCountsContext);
  const userId = user.id.toString();
  const chatToken = user.chatToken;
  const client = useMemo(
    () =>
      new StreamChat(process.env.REACT_APP_STREAM_KEY || "", {
        timeout: STREAM_CONNECTION_TIMEOUT,
      }),
    []
  );

  const safeDisconnectUser = async (chatClient: StreamChat) => {
    try {
      await chatClient.disconnectUser();
    } catch (error) {
      console.warn(
        "Could not disconnect chat service:",
        formatApiRequestError(error as ErrorType)
      );
    }
  };

  useEffect(() => {
    handleConnection(client);
    document.body.addEventListener("click", resetDisconnectionTimer);
    document.body.addEventListener("keydown", resetDisconnectionTimer);
    document.body.addEventListener("mousemove", resetDisconnectionTimer);
    document.body.addEventListener("scroll", resetDisconnectionTimer);
    document.body.addEventListener("touchstart", resetDisconnectionTimer);

    debouncedRefreshUnreadCountsRef.current = debounce(() => {
      triggerUnreadRefresh();
    }, UNREAD_REFRESH_DEBOUNCE_MS);

    return () => {
      clearTimeout(disconnectionTimeout.current);
      clearUnreadEventListeners();
      if (debouncedRefreshUnreadCountsRef.current) {
        debouncedRefreshUnreadCountsRef.current.clear();
      }
      void safeDisconnectUser(client);
      setChatClient(null);
      document.body.removeEventListener("click", resetDisconnectionTimer);
      document.body.removeEventListener("keydown", resetDisconnectionTimer);
      document.body.removeEventListener("mousemove", resetDisconnectionTimer);
      document.body.removeEventListener("scroll", resetDisconnectionTimer);
      document.body.removeEventListener("touchstart", resetDisconnectionTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerUnreadRefresh = () => {
    setIsBadgeSyncing(true);
    if (typeof refreshStreamUnreadCounts === "function") {
      Promise.resolve(refreshStreamUnreadCounts())
        .catch((error) => {
          console.warn(
            "Could not refresh unread counts:",
            formatApiRequestError(error as ErrorType)
          );
        })
        .finally(() => {
          setIsBadgeSyncing(false);
        });
      return;
    }
    setIsBadgeSyncing(false);
  };

  const handleConnection = (client: StreamChat) => {
    handleSessionCheck();
    setShowInfoModal(false);
    connectChat(client)
      .then((response) => {
        if (response && response.me) {
          setChatClient(client);
          subscribeToUnreadEvents(client);
          triggerUnreadRefresh();
          clearTimeout(disconnectionTimeout.current);
          disconnectionTimer(client);
        }
      })
      .catch((error) => {
        handleServerError(error, "Could not connect to chat service:");
      });
  };

  const connectChat = async (client: StreamChat) => {
    return await client.connectUser({ id: userId }, chatToken);
  };

  const handleReconnect = () => {
    clearUnreadEventListeners();
    handleConnection(client);
  };

  const clearUnreadEventListeners = () => {
    unreadEventUnsubscribeHandlers.current.forEach((handler) => {
      if (typeof handler === "function") {
        handler();
      } else if (handler && typeof handler.unsubscribe === "function") {
        handler.unsubscribe();
      }
    });
    unreadEventUnsubscribeHandlers.current = [];
  };

  const subscribeToUnreadEvents = (client: StreamChat) => {
    clearUnreadEventListeners();
    const eventTypes = [
      "message.new",
      "notification.message_new",
      "message.read",
      "notification.mark_read",
      "notification.mark_unread",
    ] as const;

    unreadEventUnsubscribeHandlers.current = eventTypes.map((eventType) => {
      const handler = () => {
        if (debouncedRefreshUnreadCountsRef.current) {
          debouncedRefreshUnreadCountsRef.current();
        }
      };
      return client.on(eventType, handler);
    });

    unreadEventUnsubscribeHandlers.current.push(
      client.on("connection.changed", (event: { online?: boolean }) => {
        if (event?.online === false) {
          setIsBadgeSyncing(true);
          return;
        }

        if (debouncedRefreshUnreadCountsRef.current) {
          debouncedRefreshUnreadCountsRef.current();
        }
      })
    );

    unreadEventUnsubscribeHandlers.current.push(
      client.on("connection.recovered", () => {
        if (debouncedRefreshUnreadCountsRef.current) {
          debouncedRefreshUnreadCountsRef.current();
        }
      })
    );
  };

  const disconnectionTimer = (client: StreamChat) => {
    disconnectionTimeout.current = window.setTimeout(async () => {
      clearTimeout(disconnectionTimeout.current);
      await safeDisconnectUser(client);
      setChatClient(null);
      setShowInfoModal(true);
    }, STREAM_SERVICE_TIMEOUT);
  };

  let resetDisconnectionTimer = debounce(() => {
    clearTimeout(disconnectionTimeout.current);
    disconnectionTimer(client);
  }, TIMER_DEBOUNCE_TIME);

  const handleServerError = (error: ErrorType, description: string) => {
    console.warn(description, formatApiRequestError(error));
    setShowServerErrorModal(true);
  };

  useTitle("Messaging");

  return (
    <div className={classes.pageLayout}>
      <PageHeader title="Inbox" divider />
      {chatClient && !showInfoModal && !showServerErrorModal && (
        <div className={classes.inboxWrapper}>
          {isBadgeSyncing && (
            <div className={classes.syncingTopBorder}>
              <LinearProgress color="secondary" />
            </div>
          )}
          <MessageInbox client={chatClient} />
        </div>
      )}
      <Dialog
        open={showInfoModal}
        onClose={handleReconnect}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Chat Disconnected"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            The chat has been disconnected due to inactivity. Click OK to
            reconnect.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReconnect} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <ModalServerError
        onDismiss={() => setShowServerErrorModal(false)}
        open={showServerErrorModal}
      />
    </div>
  );
};

export default MessageInboxView;
